import React, {useContext, useEffect, useState, useMemo, useRef} from "react";
import {Space, Button, Input, Select, Form, Typography, message, Card, Table, Tag as AntTag, Tooltip, Segmented} from 'antd';
import {connect, useDispatch} from "react-redux";
import {testModel, getModels, getTags} from "../../util/redux/asyncActions";
import {clearTestResult} from "../../util/redux/index";
import ManyosSpinner from "../../ManyosSpinner/ManyosSpinner";
import {AuthContext} from "../../Auth/AuthProvider";
import Tag from "../../Tag/Tag";
import AnalyzedText from "../../AnalyzedText/AnalyzedText";
import {RECOGNIZER_LABELS} from "../../util/constants";
import {PlayCircleOutlined} from "@ant-design/icons";
import RedactionChip from "../../common/RedactionChip";

const {Text} = Typography;
const {TextArea} = Input;

const select = state => {
    const files = state.request.files;
    const loading = state.request.loading;
    const testResult = state.request.testResult;
    const testDurationMs = state.request.testDurationMs;
    const analysis = state.request.analysis;
    const tags = state.request.all_entities;
    const allTags = state.request.tags;
    const models = state.request.models;
    const dynamicTagging = state.request.dynamicTagging;
    const privacyFilter = state.request.privacyFilter;
    const entities = state.request.entities;
    const config = state.request.config;
    return {
        files, models, testResult, testDurationMs, loading, tags, analysis, allTags,
        dynamicTagging, privacyFilter, entities, config
    };
};

function TestModel(props) {

    const {files, loading, testResult, testDurationMs, models, tags, analysis, allTags, dynamicTagging, privacyFilter, entities, config} = props;
    const dispatch = useDispatch();
    const userManager = useContext(AuthContext);

    useEffect(() => {
        dispatch(getModels({userManager}));
    }, []);

    const [testString, setTestString] = useState("");
    const [testMode, setTestMode] = useState("config");
    const [model, setModel] = useState();
    const [removeTags, setRemoveTags] = useState([]);

    const llmTagging = useMemo(() => {
        try {
            const parsed = JSON.parse(config);
            return parsed?.llmTagging || null;
        } catch (e) {
            return null;
        }
    }, [config]);

    const extendedModels = useMemo(() => {
        const modelList = models ? [...models] : [];
        if (llmTagging && llmTagging.url && llmTagging.model) {
            modelList.unshift({
                model: "__llm_tagging__",
                type: "llm",
                lang: "multi",
                displayName: "LLM Tagging (Gateway)"
            });
        }
        if (privacyFilter && privacyFilter.model) {
            modelList.unshift({
                model: "__privacy_filter__",
                type: "privacy-filter",
                lang: "multi",
                displayName: "Privacy Filter"
            });
        }
        if (dynamicTagging && dynamicTagging.model) {
            modelList.unshift({
                model: "__dynamic_tagging__",
                type: "gliner",
                lang: "multi",
                displayName: "Dynamic Tagging"
            });
        }
        return modelList;
    }, [models, dynamicTagging, privacyFilter, llmTagging]);

    const selectedModelDef = useMemo(() => {
        if (!model) return null;
        return extendedModels.find(m => m.model === model) || null;
    }, [model, extendedModels]);

    React.useEffect(() => {
        if (tags && tags.length > 0) {
            setRemoveTags(tags);
        }
    }, [tags]);

    React.useEffect(() => {
        if (testMode === "config") {
            const entityKeys = entities ? Object.keys(entities) : [];
            setRemoveTags(entityKeys);
            setModel(undefined);
        }
    }, [testMode]);

    // entity-driven recognizers (Dynamic Tagging / LLM): load ALL configured
    // entities when the model is selected - the user can then deselect
    React.useEffect(() => {
        if (selectedModelDef?.type === "gliner" || selectedModelDef?.type === "llm") {
            setRemoveTags(entities ? Object.keys(entities) : []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedModelDef?.type]);

    const highlightedOriginal = useMemo(() => {
        const text = testString || "";
        if (!text || analysis.length === 0) {
            return [<span key="orig">{text}</span>];
        }

        const spans = [...analysis].sort((a, b) => {
            if (a.start !== b.start) return a.start - b.start;
            const lenA = (a.end - a.start), lenB = (b.end - b.start);
            if (lenA !== lenB) return lenB - lenA;
            return (b.score || 0) - (a.score || 0);
        });

        const selected = [];
        let lastEnd = -1;
        for (const s of spans) {
            if (s.start >= lastEnd) {
                selected.push(s);
                lastEnd = s.end;
            }
        }

        const nodes = [];
        let cursor = 0;
        selected.forEach((ent, idx) => {
            if (ent.start > cursor) {
                nodes.push(<span key={`txt-${idx}-${cursor}`}>{text.slice(cursor, ent.start)}</span>);
            }
            const value = text.slice(ent.start, ent.end);
            const tagEntry = allTags.find(e => e.tag === ent.type);
            const color = tagEntry ? tagEntry.color : "#ef7721";
            nodes.push(
                <Tooltip
                    key={`tag-${idx}-${ent.start}-${ent.end}`}
                    title={`${ent.score ? (ent.score * 100).toFixed(1) + "%" : ""} ${RECOGNIZER_LABELS[ent.recognizer] || ""}`}
                >
                    <span style={{display: "inline"}}>
                        <Tag
                            tagName={ent.type}
                            mytype="TAG"
                            color={color}
                        >
                            {value}
                        </Tag>
                    </span>
                </Tooltip>
            );
            cursor = ent.end;
        });
        if (cursor < text.length) {
            nodes.push(<span key={`txt-tail-${cursor}`}>{text.slice(cursor)}</span>);
        }
        return nodes;
    }, [testString, analysis, allTags]);

    const anonymizedHighlighted = useMemo(() => {
        if (!testResult) return null;
        return testResult.split(/(<[A-Z_]+>)/g).map((part, i) => {
            const m = part.match(/^<([A-Z_]+)>$/);
            return m
                ? <RedactionChip key={i} type={m[1]} style={{margin: "0 2px"}}/>
                : <span key={i}>{part}</span>;
        });
    }, [testResult]);

    // ---- live-highlighted input (overlay technique) ----
    const backRef = useRef(null);
    const syncScroll = (e) => { if (backRef.current) backRef.current.scrollTop = e.target.scrollTop; };
    const typeColor = (t) => {
        const e = (allTags || []).find((x) => x.tag === t);
        return (e && e.color) || "#E4691A";
    };
    const inputMarks = useMemo(() => {
        const text = testString || "";
        if (!text) return null;
        if (!analysis || analysis.length === 0) return <span>{text}</span>;
        const spans = [...analysis].sort((a, b) => a.start !== b.start ? a.start - b.start : (b.end - b.start) - (a.end - a.start));
        const sel = []; let lastEnd = -1;
        for (const s of spans) { if (s.start >= lastEnd) { sel.push(s); lastEnd = s.end; } }
        const nodes = []; let cur = 0;
        sel.forEach((ent, idx) => {
            if (ent.start > cur) nodes.push(<span key={`t${idx}`}>{text.slice(cur, ent.start)}</span>);
            const c = typeColor(ent.type);
            nodes.push(<mark key={`m${idx}`} style={{background: `${c}4d`, boxShadow: `inset 0 0 0 1px ${c}66, inset 0 -2.5px 0 ${c}`, borderRadius: 4, color: "transparent"}}>{text.slice(ent.start, ent.end)}</mark>);
            cur = ent.end;
        });
        if (cur < text.length) nodes.push(<span key="tail">{text.slice(cur)}</span>);
        return nodes;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [testString, analysis, allTags]);

    const editorText = {boxSizing: "border-box", fontFamily: "inherit", fontSize: 14.5, lineHeight: 1.7, padding: "12px 14px", whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0, letterSpacing: "normal"};

    const handleTestStringChange = (e) => {
        setTestString(e.target.value);
        // stale results don't match the edited text - clear them
        if (testResult || (analysis && analysis.length > 0)) {
            dispatch(clearTestResult());
        }
    };

    const handleModelSelect = (e) => {
        const selModel = extendedModels.find(m => m.model === e);
        if (!selModel) return;

        if (selModel.type === "config") {
            const entityKeys = entities ? Object.keys(entities) : [];
            setRemoveTags(entityKeys);
        } else if (selModel.type === "gliner") {
            const entityKeys = entities ? Object.keys(entities).filter(k => entities[k]?.labels?.length > 0) : [];
            setRemoveTags(entityKeys);
        } else if (selModel.type === "privacy-filter") {
            setRemoveTags(privacyFilter?.entities ? Object.keys(privacyFilter.entities) : []);
        } else {
            const data = {
                "model": {
                    "configuration": {
                        "nlp_engine_name": "spacy",
                        "models": [
                            {
                                "lang_code": selModel.lang,
                                "model_name": (selModel.type === "custom") ? `./models/${selModel.model}` : selModel.model
                            }
                        ]
                    }
                }
            };
            dispatch(getTags({data, userManager}));
        }
        setModel(e);
    };

    const handleRemoveTags = (e) => {
        setRemoveTags(e);
    };

    const tagRender = (props) => {
        const {label, value, closable, onClose} = props;
        const onPreventMouseDown = event => {
            event.preventDefault();
            event.stopPropagation();
        };
        let tagColor;
        if (allTags && Array.isArray(allTags)) {
            const tagEntry = allTags.find(e => e.tag === value);
            if (tagEntry) {
                tagColor = tagEntry.color;
            }
        }

        return (
            <Tag
                color={tagColor}
                onMouseDown={onPreventMouseDown}
                closable={false}
                onClose={onClose}
                style={{marginInlineEnd: 4}}
            >
                {label}
            </Tag>
        );
    };

    const handleRunTest = () => {
        if (!testString || testString.length === 0) {
            message.error("Enter a test string");
            return;
        }
        if (testMode === "config") {
            dispatch(testModel({
                testString,
                modeldef: {type: "config"},
                removeTags,
                dynamicTagging,
                entities,
                userManager,
                savedConfig: config
            }));
        } else if (model) {
            const modeldef = extendedModels.find(m => m.model === model);
            // entity-driven recognizers: the Entities selection restricts what
            // is searched for - send only the selected entity types
            const selectedEntities = Object.fromEntries(
                Object.entries(entities || {}).filter(([k]) => (removeTags || []).includes(k))
            );
            if (modeldef.type === "gliner") {
                dispatch(testModel({
                    testString,
                    modeldef,
                    removeTags: [],
                    dynamicTagging,
                    entities: selectedEntities,
                    userManager
                }));
            } else if (modeldef.type === "privacy-filter") {
                dispatch(testModel({
                    testString,
                    modeldef,
                    removeTags: [],
                    privacyFilter,
                    userManager
                }));
            } else if (modeldef.type === "llm") {
                dispatch(testModel({
                    testString,
                    modeldef,
                    removeTags: [],
                    llmTagging,
                    entities: selectedEntities,
                    userManager
                }));
            } else {
                dispatch(testModel({
                    testString,
                    modeldef,
                    removeTags,
                    dynamicTagging: null,
                    entities: null,
                    userManager
                }));
            }
        } else {
            message.error("Select a model");
        }
    };

    const availableTagOptions = useMemo(() => {
        if (testMode === "config") {
            return entities
                ? Object.keys(entities).map((tag, i) => ({key: i, label: tag, value: tag}))
                : [];
        }
        if (selectedModelDef?.type === "gliner" || selectedModelDef?.type === "llm") {
            return entities
                ? Object.keys(entities).map((tag, i) => ({key: i, label: tag, value: tag}))
                : [];
        }
        return tags ? tags.map((tag, i) => ({key: i, label: tag, value: tag})) : [];
    }, [selectedModelDef, tags, entities]);

    const detectionColumns = [
        {title: "Entity", dataIndex: "type", key: "type", width: 120, render: (t) => <Text strong>{t}</Text>},
        {
            title: "Value", key: "value", render: (_, r) => (
                <Text code>{testString.slice(r.start, r.end)}</Text>
            )
        },
        {
            title: "Source", dataIndex: "recognizer", key: "recognizer", width: 150,
            render: (r) => (
                <AntTag color={r === "gliner" ? "blue" : r === "privacy-filter" ? "purple" : r === "llm" ? "orange" : r === "pattern" ? "cyan" : "green"} style={{fontWeight: 500}}>
                    {RECOGNIZER_LABELS[r] || r || "\u2014"}
                </AntTag>
            )
        },
        {
            title: "Confidence", dataIndex: "score", key: "score", width: 100,
            render: (s) => s ? <Text type={s >= 0.8 ? "success" : s >= 0.5 ? "warning" : "danger"}>{(s * 100).toFixed(1)}%</Text> : "\u2014"
        },
        {title: "Model", dataIndex: "model", key: "model", width: 180, render: (m) => <Text type="secondary">{m}</Text>}
    ];

    return (
        <ManyosSpinner spinning={loading}>
            <div style={{maxWidth: 1080, margin: "0 auto", padding: "8px 0 24px"}}>

                {(!config || config === "{}") && (
                    <div style={{
                        padding: "12px 16px",
                        background: "var(--sc-brand-soft)",
                        border: "1px solid var(--sc-border-strong)",
                        borderRadius: 10,
                        color: "var(--sc-brand-ink)",
                        marginBottom: 20,
                        fontSize: 13,
                    }}>
                        No configuration found. Please set up your models and entities in the Configuration tab first.
                    </div>
                )}

                <div className="sc-panel" style={{marginBottom: 20}}>
                    <div className="sc-panel-cap">Test model — see exactly what gets hidden</div>
                <Form layout="vertical">
                    <Form.Item label="Test Mode">
                        <Segmented
                            value={testMode}
                            onChange={setTestMode}
                            options={[
                                {label: "Saved Config", value: "config"},
                                {label: "Specific Model", value: "model"},
                            ]}
                        />
                    </Form.Item>

                    <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20}}>
                        {testMode === "model" && (
                            <Form.Item label="Model">
                                <Select
                                    onSelect={handleModelSelect}
                                    value={model}
                                    placeholder="Select a model..."
                                    options={extendedModels.map((m, i) => ({
                                        key: i,
                                        label: m.displayName || m.model,
                                        value: m.model
                                    }))}
                                />
                            </Form.Item>
                        )}

                        {availableTagOptions.length > 0 && (
                            <Form.Item label={testMode === "config" || selectedModelDef?.type === "gliner" || selectedModelDef?.type === "llm" ? "Entities" : "Remove Tags"}>
                                <Select
                                    mode="multiple"
                                    allowClear
                                    tagRender={tagRender}
                                    value={removeTags}
                                    onChange={handleRemoveTags}
                                    options={availableTagOptions}
                                />
                            </Form.Item>
                        )}
                    </div>

                    <Form.Item label="Your text">
                        <div style={{position: "relative", borderRadius: 10, border: "1px solid var(--sc-border-strong)", background: "var(--sc-surface)"}}>
                            <div ref={backRef} aria-hidden="true" style={{...editorText, position: "absolute", inset: 0, overflow: "hidden", color: "transparent", pointerEvents: "none", userSelect: "none"}}>
                                {inputMarks}
                            </div>
                            <textarea
                                value={testString}
                                onChange={handleTestStringChange}
                                onScroll={syncScroll}
                                rows={3}
                                aria-label="Enter text to test"
                                placeholder="Enter text to analyze…"
                                style={{...editorText, position: "relative", display: "block", width: "100%", minHeight: 84, background: "transparent", color: "var(--sc-ink)", caretColor: "var(--sc-brand)", border: "none", outline: "none", resize: "vertical"}}
                            />
                        </div>
                        {analysis && analysis.length > 0 && (
                            <div style={{display: "flex", gap: 16, flexWrap: "wrap", marginTop: 10, fontSize: 12, color: "var(--sc-ink-3)"}}>
                                {[...new Set(analysis.map(a => a.type))].map(t => (
                                    <span key={t} style={{display: "inline-flex", alignItems: "center", gap: 6}}>
                                        <i style={{width: 10, height: 10, borderRadius: 3, background: typeColor(t), display: "inline-block"}}/>{t}
                                    </span>
                                ))}
                            </div>
                        )}
                    </Form.Item>

                    <Form.Item>
                        <Button
                            onClick={handleRunTest}
                            type="primary"
                            size="large"
                            icon={<PlayCircleOutlined/>}
                            aria-label="Run model test"
                        >
                            Run Test
                        </Button>
                    </Form.Item>
                </Form>
                </div>

                {testResult && (
                    <div className="sc-grid2" style={{marginBottom: 20}}>
                        <div className="sc-panel">
                            <div className="sc-panel-cap">Result — anonymized</div>
                            <div style={{lineHeight: 2.0, whiteSpace: "pre-wrap", fontSize: 14.5, color: "var(--sc-ink)"}}>
                                {anonymizedHighlighted}
                            </div>
                        </div>
                        <div className="sc-panel">
                            <div className="sc-panel-cap" style={{justifyContent: "space-between"}}>
                                <span>Detected · {analysis.length} {analysis.length === 1 ? "entity" : "entities"}</span>
                                {testDurationMs !== undefined && (
                                    <span style={{textTransform: "none", letterSpacing: 0, fontWeight: 500}}>{(testDurationMs / 1000).toFixed(2)}s</span>
                                )}
                            </div>
                            {analysis.length === 0 ? (
                                <span className="sc-dim">No entities detected.</span>
                            ) : (
                                <div style={{display: "flex", flexDirection: "column", gap: 8}}>
                                    {analysis.map((a, i) => {
                                        const c = typeColor(a.type);
                                        const score = a.score || 0;
                                        return (
                                            <div key={i} style={{display: "flex", alignItems: "center", gap: 10, fontSize: 13, padding: "8px 11px", background: "var(--sc-surface-2)", border: "1px solid var(--sc-border)", borderRadius: 10}}>
                                                <span style={{fontFamily: "var(--sc-mono)", fontSize: 11, fontWeight: 700, color: c, background: `${c}1f`, padding: "2px 7px", borderRadius: 6, flex: "none"}}>{a.type}</span>
                                                <span style={{color: "var(--sc-ink-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 150}}>{testString.slice(a.start, a.end)}</span>
                                                <span style={{marginLeft: "auto"}}/>
                                                <span style={{color: "var(--sc-ink-3)", fontSize: 11.5}}>{RECOGNIZER_LABELS[a.recognizer] || a.recognizer || ""}</span>
                                                <div style={{width: 56, height: 6, borderRadius: 99, background: "var(--sc-inset)", overflow: "hidden", flex: "none"}}>
                                                    <i style={{display: "block", height: "100%", width: `${Math.round(score * 100)}%`, background: score >= 0.8 ? "var(--sc-ok)" : score >= 0.5 ? "#C08A2E" : "var(--sc-danger)", borderRadius: 99}}/>
                                                </div>
                                                <span style={{fontVariantNumeric: "tabular-nums", color: "var(--sc-ink-3)", fontSize: 11.5, width: 32, textAlign: "right"}}>{Math.round(score * 100)}%</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </ManyosSpinner>
    );
}

export default connect(select)(TestModel);
